// ============================================================
// Chess Arena — Daily Tasks Screen
// Overlay showing 3 daily tasks + daily chest progress
// ============================================================

import type { Screen } from '../screenManager';
import type { ActiveTask } from '../../core/types';

const DIFFICULTY_CLASSES: Record<string, string> = {
  easy: 'task-card-easy',
  medium: 'task-card-medium',
  hard: 'task-card-hard',
};

const DIFFICULTY_COLORS: Record<string, string> = {
  easy: 'var(--ca-green)',
  medium: 'var(--ca-orange)',
  hard: 'var(--ca-red)',
};

export class DailyTasksScreen implements Screen {
  readonly name = 'dailyTasks';
  private container!: HTMLElement;
  private tasks: ActiveTask[] = [];
  private dailyChestClaimed = false;

  // Callbacks
  private onClose: (() => void) | null = null;
  private onClaimTask: ((taskId: string) => void) | null = null;
  private onClaimDailyChest: (() => void) | null = null;

  setOnClose(cb: () => void): void {
    this.onClose = cb;
  }

  setOnClaimTask(cb: (taskId: string) => void): void {
    this.onClaimTask = cb;
  }

  setOnClaimDailyChest(cb: () => void): void {
    this.onClaimDailyChest = cb;
  }

  init(container: HTMLElement): void {
    this.container = container;
    container.classList.add('tasks-screen');
    // Content built dynamically in show()
  }

  show(data?: Record<string, unknown>): void {
    if (data?.tasks) {
      this.tasks = data.tasks as ActiveTask[];
    }
    if (data?.dailyChestClaimed !== undefined) {
      this.dailyChestClaimed = data.dailyChestClaimed as boolean;
    }

    this.render();
  }

  hide(): void {
    this.container.innerHTML = '';
  }

  update(): void {
    // Update reset timer
    const timerEl = this.container.querySelector('[data-ref="resetTimer"]');
    if (timerEl) {
      timerEl.textContent = `Resets in ${this.getTimeUntilReset()}`;
    }
  }

  destroy(): void {
    this.container.innerHTML = '';
  }

  private render(): void {
    const completedCount = this.tasks.filter(
      (t) => t.progress >= t.config.target
    ).length;
    const allDone = completedCount >= 3;

    this.container.innerHTML = `
      <div class="ca-backdrop">
        <div class="tasks-overlay">
          <div class="tasks-header">
            <div class="tasks-title">Daily Tasks</div>
            <div style="display: flex; align-items: center; gap: 12px;">
              <div class="tasks-reset-timer" data-ref="resetTimer">Resets in ${this.getTimeUntilReset()}</div>
              <button class="ca-close-btn" data-ref="closeBtn">\u2715</button>
            </div>
          </div>

          ${this.tasks.map((task) => this.renderTaskCard(task)).join('')}

          <!-- Daily Chest -->
          <div class="daily-chest">
            <div class="daily-chest-icon">${allDone && !this.dailyChestClaimed ? '\uD83C\uDF81' : '\uD83D\uDD12'}</div>
            <div class="daily-chest-title">Daily Chest</div>
            <div class="daily-chest-progress">${completedCount}/3 Tasks Complete</div>
            <div class="ca-progress ca-progress-gold" style="width: 80%; max-width: 200px;">
              <div class="ca-progress-fill" style="width: ${(completedCount / 3) * 100}%;"></div>
            </div>
            ${allDone && !this.dailyChestClaimed
              ? '<button class="ca-btn ca-btn-gold ca-btn-sm" data-ref="claimChestBtn">Open Chest!</button>'
              : this.dailyChestClaimed
                ? '<div style="font-size: 13px; color: var(--ca-green); font-weight: 600;">\u2713 Claimed</div>'
                : ''
            }
          </div>
        </div>
      </div>
    `;

    // Event listeners
    const closeBtn = this.container.querySelector('[data-ref="closeBtn"]');
    closeBtn?.addEventListener('click', () => this.onClose?.());

    // Backdrop click to close
    const backdrop = this.container.querySelector('.ca-backdrop');
    backdrop?.addEventListener('click', (e) => {
      if (e.target === backdrop) this.onClose?.();
    });

    // Claim task buttons
    this.container.querySelectorAll('[data-claim-task]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const taskId = btn.getAttribute('data-claim-task');
        if (taskId) {
          this.onClaimTask?.(taskId);
          // Trigger a mini coin-fly animation from the button
          this.spawnClaimAnimation(btn as HTMLElement);
        }
      });
    });

    // Claim daily chest
    const claimChestBtn = this.container.querySelector('[data-ref="claimChestBtn"]');
    claimChestBtn?.addEventListener('click', () => this.onClaimDailyChest?.());
  }

  private renderTaskCard(task: ActiveTask): string {
    const { config, progress, claimed } = task;
    const isComplete = progress >= config.target;
    const pct = Math.min(100, (progress / config.target) * 100);
    const diffClass = DIFFICULTY_CLASSES[config.difficulty] ?? '';
    const progressColor = DIFFICULTY_COLORS[config.difficulty] ?? 'var(--ca-green)';

    return `
      <div class="task-card ${diffClass}">
        <div class="task-card-top">
          <span class="task-description">${config.description}</span>
          <span class="task-reward">\uD83D\uDCB0 ${config.reward.coins} + ${config.reward.xp} XP</span>
        </div>
        <div class="task-progress-row">
          <div class="ca-progress" style="height: 10px;">
            <div class="ca-progress-fill" style="width: ${pct}%; background: ${progressColor};"></div>
          </div>
          <span class="task-progress-text">${progress}/${config.target}</span>
        </div>
        ${isComplete && !claimed
          ? `<button class="ca-btn ca-btn-green ca-btn-sm task-claim-btn" data-claim-task="${config.taskId}">Claim</button>`
          : claimed
            ? '<div style="align-self: flex-end; font-size: 12px; color: var(--ca-green); font-weight: 600;">\u2713 Claimed</div>'
            : ''
        }
      </div>
    `;
  }

  private getTimeUntilReset(): string {
    const now = new Date();
    const tomorrow = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + 1,
      0, 0, 0
    ));
    const diff = tomorrow.getTime() - now.getTime();
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    return `${hours}h ${mins}m`;
  }

  private spawnClaimAnimation(btn: HTMLElement): void {
    const rect = btn.getBoundingClientRect();
    const startX = rect.left + rect.width / 2;
    const startY = rect.top;

    for (let i = 0; i < 5; i++) {
      const coin = document.createElement('div');
      coin.className = 'coin-fly';
      coin.textContent = '\uD83D\uDCB0';
      coin.style.left = `${startX}px`;
      coin.style.top = `${startY}px`;

      const flyX = (Math.random() - 0.5) * 80;
      coin.style.setProperty('--fly-x', `${flyX}px`);
      coin.style.setProperty('--fly-y', '-60px');
      coin.style.setProperty('--fly-end-x', `${flyX}px`);
      coin.style.setProperty('--fly-end-y', '-150px');
      coin.style.animationDelay = `${i * 0.08}s`;

      document.body.appendChild(coin);
      setTimeout(() => coin.remove(), 1000 + i * 80);
    }
  }
}
